import { X, Warning } from "@phosphor-icons/react";

/**
 * Owner-only destructive-action modal.
 *
 * Two visual states controlled by `blocked`:
 *   - blocked=false → confirm permanent deletion
 *   - blocked=true  → delete is prevented (shows reason + "View Products" link)
 *
 * Props:
 *   open, onClose, onConfirm, title, description,
 *   confirmLabel (default "DELETE"),
 *   blocked (bool), blockedTitle, blockedMessage, viewHref, viewLabel
 */
export default function ConfirmDeleteModal({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmLabel = "Delete",
    blocked = false,
    blockedTitle,
    blockedMessage,
    viewHref,
    viewLabel = "View Products",
    busy = false,
}) {
    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm px-4"
            onClick={onClose}
            data-testid="confirm-delete-modal"
        >
            <div
                className="relative w-full max-w-md bg-white border border-mist rounded-sm shadow-xl p-6 md:p-7"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-3 right-3 text-neutral-400 hover:text-ink p-1.5"
                    data-testid="confirm-delete-close"
                >
                    <X size={16} weight="light" />
                </button>

                {blocked ? (
                    <>
                        <div className="flex items-start gap-3">
                            <Warning size={22} weight="light" className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <h3 className="font-serif text-xl leading-snug">{blockedTitle || "Still in use"}</h3>
                                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                                    {blockedMessage}
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="inline-flex items-center gap-1 border border-mist text-neutral-600 px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark hover:text-ink"
                                data-testid="confirm-delete-cancel"
                            >
                                Cancel
                            </button>
                            {viewHref && (
                                <a
                                    href={viewHref}
                                    onClick={onClose}
                                    className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep"
                                    data-testid="confirm-delete-view"
                                >
                                    {viewLabel}
                                </a>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <h3 className="font-serif text-xl leading-snug">{title}</h3>
                        <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                            {description}
                        </p>
                        <div className="mt-6 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={busy}
                                className="inline-flex items-center gap-1 border border-mist text-neutral-600 px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark hover:text-ink disabled:opacity-60"
                                data-testid="confirm-delete-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={onConfirm}
                                disabled={busy}
                                className="inline-flex items-center gap-1 bg-red-600 text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-red-700 disabled:opacity-60"
                                data-testid="confirm-delete-confirm"
                            >
                                {confirmLabel}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
